import { Document, Page, pdfjs } from 'react-pdf';
import { Box, CircularProgress } from '@mui/material';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

/**
 * pdf.js-Worker aus dem eigenen Build (Ticket #1293).
 *
 * Vorher kam er von `//unpkg.com/...`. Ohne Internetzugang funktionierte damit
 * keine einzige PDF-Vorschau in DiNo — diese Komponente steckt im
 * Dokumentassistenten, im Sortierdialog, im Mitlese-Panel und in der
 * Seitenleiste. Für ein Notariat mit abgeschottetem Netz war die Vorschau also
 * unbenutzbar, und das schemalose `//` lud den Worker bei einer HTTP-Instanz
 * über HTTP.
 *
 * Die Datei landet über `prestart`/`prebuild` aus node_modules in `public/`
 * (siehe scripts/copyPdfWorker.js) und passt damit immer zur installierten
 * pdf.js-Version. `PUBLIC_URL` deckt Installationen in einem Unterpfad ab.
 *
 * `new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url)` wäre der
 * kürzere Weg, scheitert hier aber am Babel-Setup von react-scripts — der
 * Versuch stand vorher auskommentiert an dieser Stelle.
 */
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface IProps {
  pdfFile: Blob | string;
  numPages: number;
  pageNumber: number;
  setNumPages: (numPages: number) => void;
  scale: number;
  scrollable?: boolean;
}

export const PdfDocument = ({
  pdfFile,
  setNumPages,
  numPages,
  pageNumber,
  scale,
  scrollable = false,
}: IProps) => {
  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  /**
   * react-pdf zeigt ohne `error`-Prop nur seinen Standardtext
   * „Failed to load PDF file." — unabhaengig davon, WORAN es lag: kaputte
   * Bytes, ein 404 auf die Datei, ein nicht ladbarer Worker oder eine
   * Versionsabweichung zwischen API und Worker sehen alle gleich aus. Das
   * kostete schon mehrfach eine Fehlersuche im Blindflug, deshalb landet der
   * echte Grund hier in der Konsole.
   */
  const onDocumentLoadError = (error: Error) => {
    console.error('[PdfDocument] PDF konnte nicht geladen werden:', error);
  };

  const styles = {
    width: '100%',
    '& .react-pdf__Page__canvas': {
      width: '100% !important',
      height: 'auto !important',
    },
  };

  if (scrollable) {
    return (
      <Box sx={scale === 1 ? styles : undefined}>
        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
        >
          {Array.from(new Array(numPages), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              loading={<CircularProgress size={20} />}
              scale={scale}
            />
          ))}
        </Document>
      </Box>
    );
  }

  return (
    <Box sx={scale === 1 ? styles : undefined}>
      <Document
        file={pdfFile}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
      >
        <Page
          pageNumber={pageNumber}
          loading={<CircularProgress size={20} />}
          scale={scale}
        />
      </Document>
    </Box>
  );
};
